import { Injectable } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register Chart.js components
Chart.register(...registerables, ChartDataLabels);

export interface MonthlyStatusData {
  total: number;
  green: number;
  orange: number;
  red: number;
  total_wert?: number;
}

@Injectable({
  providedIn: 'root'
})
export class TrendChartService {

  /**
   * Generate a stacked bar chart as a base64 PNG image.
   * The chart shows monthly system counts split by status (green/orange/red).
   *
   * @param monthlyData  Object with keys 1-12, each having {total, green, orange, red}
   * @param year         Year label (e.g. 2024)
   * @param title        Optional title (e.g. indicator type name)
   * @returns            base64 PNG string (with data:image/png;base64, prefix)
   */
  async generateStackedBarChart(
    monthlyData: { [month: number]: MonthlyStatusData },
    year: number,
    title?: string
  ): Promise<string> {
    // Create an off-screen canvas
    const canvas = document.createElement('canvas');
    canvas.width = 760;
    canvas.height = 340;

    // Prepare data arrays for months 1-12
    const labels = Array.from({ length: 12 }, (_, i) => String(i + 1));
    const greenData: number[] = [];
    const orangeData: number[] = [];
    const redData: number[] = [];

    // Find max total for Y axis
    let maxTotal = 0;
    for (let m = 1; m <= 12; m++) {
      const d = monthlyData[m] || { total: 0, green: 0, orange: 0, red: 0 };
      greenData.push(d.green);
      orangeData.push(d.orange);
      redData.push(d.red);
      if (d.total > maxTotal) maxTotal = d.total;
    }

    // Y-axis max with some headroom
    const yMax = maxTotal > 0 ? Math.ceil(maxTotal * 1.15) : 20;

    const chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: '>= SW',
            data: redData,
            backgroundColor: '#FF0000',
            borderWidth: 0,
            order: 3
          },
          {
            label: '< SW',
            data: orangeData,
            backgroundColor: '#FF9900',
            borderWidth: 0,
            order: 2
          },
          {
            label: 'kein Befall',
            data: greenData,
            backgroundColor: '#339900',
            borderWidth: 0,
            order: 1
          }
        ]
      },
      options: {
        responsive: false,
        animation: false,
        scales: {
          x: {
            stacked: true,
            title: {
              display: true,
              text: 'Monat',
              font: { size: 11 }
            },
            grid: { display: false }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            max: yMax,
            title: {
              display: true,
              text: 'Systeme (Kontrollierte)',
              font: { size: 11 }
            },
            ticks: {
              stepSize: Math.max(1, Math.ceil(yMax / 10))
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              font: { size: 10 }
            }
          },
          title: {
            display: false
          },
          datalabels: {
            display: (context: any) => {
              return context.dataset.data[context.dataIndex] > 0;
            },
            color: '#fff',
            font: {
              weight: 'bold' as const,
              size: 10
            },
            anchor: 'center' as const,
            align: 'center' as const,
            formatter: (value: number) => value > 0 ? String(value) : ''
          }
        }
      },
      plugins: [ChartDataLabels]
    });

    // Also render the total on top of each bar
    // We'll use a custom plugin for that
    // For now, the stacked bars with data labels inside should be sufficient

    // Wait a tick for rendering
    await new Promise(resolve => setTimeout(resolve, 50));

    // Convert to base64
    const base64 = canvas.toDataURL('image/png');

    // Destroy chart to free memory
    chart.destroy();

    return base64;
  }

  /**
   * Generate a detail line chart for "Stück" (count-based) indicators.
   * Shows monthly befall values as a line with data points, with an optional
   * Vorjahr (previous year) comparison line.
   *
   * Matches the reference style: teal dashed line for current year,
   * gray dashed line for Vorjahr, with data labels at each point.
   *
   * @param currentYearData   Monthly data for the primary year
   * @param previousYearData  Monthly data for the comparison year (Vorjahr), or empty
   * @param currentYear       Primary year label
   * @param previousYear      Comparison year label
   * @param einheit           Unit label (e.g. "Stück", "Stk")
   * @returns                 base64 PNG string
   */
  async generateDetailLineChart(
    currentYearData: { [month: number]: MonthlyStatusData },
    previousYearData: { [month: number]: MonthlyStatusData },
    currentYear: number,
    previousYear: number,
    einheit?: string
  ): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = 760;
    canvas.height = 340;

    const labels = Array.from({ length: 12 }, (_, i) => String(i + 1));
    const currentData: number[] = [];
    const prevData: number[] = [];
    const unitLabel = einheit || 'Stück';

    let maxVal = 0;
    const hasPrevYear = previousYearData && Object.keys(previousYearData).length > 0;

    for (let m = 1; m <= 12; m++) {
      const cVal = (currentYearData[m]?.total_wert) ?? 0;
      currentData.push(cVal);
      if (cVal > maxVal) maxVal = cVal;

      if (hasPrevYear) {
        const pVal = (previousYearData[m]?.total_wert) ?? 0;
        prevData.push(pVal);
        if (pVal > maxVal) maxVal = pVal;
      }
    }

    const yMax = maxVal > 0 ? Math.ceil(maxVal * 1.15) : 10;

    const datasets: any[] = [
      {
        label: `Befall: ${unitLabel}`,
        data: currentData,
        borderColor: '#26C6DA',
        backgroundColor: '#26C6DA',
        pointBackgroundColor: '#26C6DA',
        pointBorderColor: '#26C6DA',
        pointRadius: 5,
        pointHoverRadius: 7,
        borderWidth: 2,
        borderDash: [6, 4],
        tension: 0,
        fill: false,
        order: 1
      }
    ];

    if (hasPrevYear) {
      datasets.push({
        label: 'Vorjahr',
        data: prevData,
        borderColor: '#9E9E9E',
        backgroundColor: '#9E9E9E',
        pointBackgroundColor: '#9E9E9E',
        pointBorderColor: '#9E9E9E',
        pointRadius: 4,
        pointHoverRadius: 6,
        pointStyle: 'crossRot',
        borderWidth: 1.5,
        borderDash: [4, 4],
        tension: 0,
        fill: false,
        order: 2
      });
    }

    const chart = new Chart(canvas, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: false,
        animation: false,
        scales: {
          x: {
            title: { display: false },
            grid: { display: false }
          },
          y: {
            beginAtZero: true,
            max: yMax,
            title: {
              display: true,
              text: 'Menge',
              font: { size: 11 }
            },
            ticks: {
              stepSize: Math.max(1, Math.ceil(yMax / 10))
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: true,
              font: { size: 10 }
            }
          },
          title: { display: false },
          datalabels: {
            display: true,
            color: (context: any) => {
              return context.datasetIndex === 0 ? '#00838F' : '#757575';
            },
            font: {
              weight: 'bold' as const,
              size: 10
            },
            anchor: 'end' as const,
            align: 'top' as const,
            offset: 2,
            formatter: (value: number) => String(value)
          }
        }
      },
      plugins: [ChartDataLabels]
    });

    await new Promise(resolve => setTimeout(resolve, 50));
    const base64 = canvas.toDataURL('image/png');
    chart.destroy();
    return base64;
  }

  /**
   * Generate a percentage bar chart for percentage-based indicators.
   * Shows system counts per month with bar color based on the percentage
   * of "green" (OK) systems. Higher green percentage = darker green bar.
   *
   * Matches the reference style: graduated green bars with data labels on top.
   *
   * @param monthlyData  Monthly data with {total, green, orange, red}
   * @param year         Year label
   * @returns            base64 PNG string
   */
  async generatePercentageBarChart(
    monthlyData: { [month: number]: MonthlyStatusData },
    year: number
  ): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = 760;
    canvas.height = 340;

    const labels = Array.from({ length: 12 }, (_, i) => String(i + 1));
    const totals: number[] = [];
    const barColors: string[] = [];

    let maxTotal = 0;

    for (let m = 1; m <= 12; m++) {
      const d = monthlyData[m] || { total: 0, green: 0, orange: 0, red: 0 };
      totals.push(d.total);
      if (d.total > maxTotal) maxTotal = d.total;

      // Compute green percentage and derive bar color
      const pct = d.total > 0 ? (d.green / d.total) * 100 : 100;
      barColors.push(this.getPercentageColor(pct));
    }

    const yMax = maxTotal > 0 ? Math.ceil(maxTotal * 1.15) : 20;

    const chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: 'Systeme',
          data: totals,
          backgroundColor: barColors,
          borderColor: barColors.map(c => c),
          borderWidth: 1
        }]
      },
      options: {
        responsive: false,
        animation: false,
        scales: {
          x: {
            title: {
              display: true,
              text: 'Monat',
              font: { size: 11 }
            },
            grid: { display: false }
          },
          y: {
            beginAtZero: true,
            max: yMax,
            title: {
              display: true,
              text: 'Systeme',
              font: { size: 11 }
            },
            ticks: {
              stepSize: Math.max(1, Math.ceil(yMax / 10))
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              generateLabels: () => {
                return [
                  { text: '100 %', fillStyle: '#2E7D32', strokeStyle: '#2E7D32', lineWidth: 0 },
                  { text: '75 %',  fillStyle: '#43A047', strokeStyle: '#43A047', lineWidth: 0 },
                  { text: '50 %',  fillStyle: '#66BB6A', strokeStyle: '#66BB6A', lineWidth: 0 },
                  { text: '25 %',  fillStyle: '#A5D6A7', strokeStyle: '#A5D6A7', lineWidth: 0 },
                  { text: '10 %',  fillStyle: '#C8E6C9', strokeStyle: '#C8E6C9', lineWidth: 0 },
                  { text: '0 %',   fillStyle: '#E8F5E9', strokeStyle: '#E8F5E9', lineWidth: 0 }
                ];
              },
              usePointStyle: true,
              pointStyle: 'circle',
              font: { size: 9 }
            }
          },
          title: { display: false },
          datalabels: {
            display: (context: any) => context.dataset.data[context.dataIndex] > 0,
            color: '#333',
            font: {
              weight: 'bold' as const,
              size: 10
            },
            anchor: 'end' as const,
            align: 'top' as const,
            offset: 2,
            formatter: (value: number) => String(value)
          }
        }
      },
      plugins: [ChartDataLabels]
    });

    await new Promise(resolve => setTimeout(resolve, 50));
    const base64 = canvas.toDataURL('image/png');
    chart.destroy();
    return base64;
  }

  /**
   * Map a green-percentage value to a graduated green color.
   * 100% green = darkest green, 0% green = lightest green.
   */
  private getPercentageColor(greenPct: number): string {
    if (greenPct >= 95) return '#2E7D32';
    if (greenPct >= 75) return '#43A047';
    if (greenPct >= 50) return '#66BB6A';
    if (greenPct >= 25) return '#A5D6A7';
    if (greenPct >= 10) return '#C8E6C9';
    return '#E8F5E9';
  }

  /**
   * Check if an einheit string represents a percentage-based unit.
   */
  private isPercentEinheit(einheit: string): boolean {
    if (!einheit) return false;
    const lower = einheit.toLowerCase().trim();
    return lower === '%' || lower.includes('prozent') || lower.includes('percent');
  }

  /**
   * Generate all charts needed for a given report type.
   *
   * For auswertung_trend this generates:
   *   - Stacked bar charts (status distribution) for current + previous year
   *   - Detail charts on the right side:
   *       - "Stück" indicators → line chart with befall values + Vorjahr comparison
   *       - "%" indicators → percentage bar chart with green-percentage coloring
   *
   * @param byCode       The byCode data from the trend API response
   * @param currentYear  Current year number
   * @param previousYear Previous year number
   * @param reportType   'auswertung_trend' | 'periodenbericht' | 'trend_matrix'
   * @returns Object mapping chart keys to base64 PNG strings
   */
  async generateAllCharts(
    byCode: { [code: string]: any },
    currentYear: number,
    previousYear: number,
    reportType: string
  ): Promise<{ [key: string]: string }> {
    const charts: { [key: string]: string } = {};

    if (reportType === 'trend_matrix') {
      // No charts needed for matrix
      return charts;
    }

    for (const code of Object.keys(byCode)) {
      const codeData = byCode[code];
      const monthly = codeData.monthly || {};

      // Current year stacked bar chart
      if (monthly[currentYear]) {
        const key = `${code}_${currentYear}`;
        charts[key] = await this.generateStackedBarChart(
          monthly[currentYear],
          currentYear,
          `${codeData.label || code} ${currentYear}`
        );
      }

      // Previous year stacked bar chart (for auswertung_trend)
      if (reportType === 'auswertung_trend' && monthly[previousYear]) {
        const key = `${code}_${previousYear}`;
        charts[key] = await this.generateStackedBarChart(
          monthly[previousYear],
          previousYear,
          `${codeData.label || code} ${previousYear}`
        );
      }

      // Detail charts — only for auswertung_trend
      if (reportType === 'auswertung_trend') {
        const einheit = codeData.einheit || 'Stk';
        const isPercent = this.isPercentEinheit(einheit);

        if (isPercent) {
          // Percentage bar chart for each year
          if (monthly[currentYear]) {
            charts[`${code}_${currentYear}_detail`] = await this.generatePercentageBarChart(
              monthly[currentYear], currentYear
            );
          }
          if (monthly[previousYear]) {
            charts[`${code}_${previousYear}_detail`] = await this.generatePercentageBarChart(
              monthly[previousYear], previousYear
            );
          }
        } else {
          // Line chart for Stück — current year with Vorjahr overlay
          if (monthly[currentYear]) {
            charts[`${code}_${currentYear}_detail`] = await this.generateDetailLineChart(
              monthly[currentYear],
              monthly[previousYear] || {},
              currentYear,
              previousYear,
              einheit
            );
          }
          // Previous year line chart (standalone, no comparison available)
          if (monthly[previousYear]) {
            charts[`${code}_${previousYear}_detail`] = await this.generateDetailLineChart(
              monthly[previousYear],
              {},
              previousYear,
              previousYear - 1,
              einheit
            );
          }
        }
      }
    }

    return charts;
  }
}
